import { Button, Col, Form, Input, message, Modal, Row, Space, Typography } from "antd";
import React, { useCallback, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { CreditDebitStatus, StatementTitle } from "../../../types/reconciliation";
import StatementTitlesTable from "../statementTitlesTable/StatementTitlesTable";
import { StatementTitlesTableProvider } from "../../../context/StatementTitlesTableContext";
import { useConfirmTitlesMutation } from "../../../redux/api/reconciliationsApiSlice";
import { isEmpty } from "lodash";
import { MassiveStatementTitlesGroupSelectionButton } from "./MassiveStatementTitlesGroupSelectionButton";

const { Title } = Typography;

export const mergeTitles = (titles: StatementTitle[], newTitles: StatementTitle[]) => {
  const uniqueMap = new Map<string, StatementTitle>();
  titles.forEach(title => {
    uniqueMap.set(title.uuid, title);
  });

  newTitles.forEach(group => {
    if (!uniqueMap.has(group.uuid)) {
      uniqueMap.set(group.uuid, group);
    }
  });

  return Array.from(uniqueMap.values());
};


export const CreateStandardRemittanceModalButton = () => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [open, setOpen] = useState(false);
  const [showConfirmedTitles, setShowConfirmedTitles] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState<StatementTitle[]>([]);
  const [selectedTitlesPage, setSelectedTitlesPage] = useState<number>(1);

  const [confirmTitles, { isLoading }] = useConfirmTitlesMutation();

  const handleCreationModalClose = useCallback(() => {
    form.resetFields();
    setSelectedTitles([]);
    setOpen(false);
    setShowConfirmedTitles(false);
  }, [form]);


  const handleMassiveSelection = useCallback((groups: StatementTitle[]) => {
    setSelectedTitles(titles => {
      return mergeTitles(titles, groups);
    });
  }, []);

  return <>
    {contextHolder}
    <Modal
      open={open}
      title={"Crea rimessa"}
      onCancel={handleCreationModalClose}
      okText={!isEmpty(selectedTitles) ? `Conferma titoli (${selectedTitles.length}) e crea rimessa` : "Conferma titoli e crea rimessa"}
      width={"90dvw"}
      footer={
        <Space size={"small"}>
          <Button
            onClick={handleCreationModalClose}>
            Annulla
          </Button>
          <Button
            disabled={isEmpty(selectedTitles)}
            onClick={() => {
              setSelectedTitlesPage(1);
              setShowConfirmedTitles(show => !show);
            }}>
            {showConfirmedTitles ? "Vedi tutti i titoli" : "Vedi titoli selezionati"}
          </Button>
          <Button
            disabled={isEmpty(selectedTitles) || isLoading}
            loading={isLoading}
            type={"primary"}
            onClick={() => {
              form.validateFields().then((values) => {
                confirmTitles({
                  name: values.name,
                  statement_title_groups: selectedTitles.map(s => s.uuid)
                }).unwrap()
                  .then(() => {
                    handleCreationModalClose();
                  }).catch((err) => {
                  console.error("could not confirm titles due to an error: ", err);
                  messageApi.error("Errore nella conferma dei titoli");
                });
              });
            }}
          >{!isEmpty(selectedTitles) ? `Conferma titoli (${selectedTitles.length}) e crea rimessa` : "Conferma titoli e crea rimessa"}</Button>
        </Space>
      }

    >
      <Form name={"confirmTitlesForm"} layout={"vertical"} form={form} style={{ maxWidth: 600 }}>
        <Form.Item name={"name"} label={"Nome rimessa:"} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
      </Form>
      <Row gutter={[16, 16]} justify={"space-between"} align={"middle"} style={{ marginBottom: "1rem" }}>
        <Col>
          <Title level={5}>{showConfirmedTitles ? "Titoli selezionati" : "Titoli broker"}</Title>
        </Col>
        <Col>
          <Space size={"small"}>
            <MassiveStatementTitlesGroupSelectionButton buttonProps={{ content: "Seleziona titoli in stato OK" }}
                                                        status={CreditDebitStatus.OK}
                                                        afterFetch={handleMassiveSelection} />
            <MassiveStatementTitlesGroupSelectionButton buttonProps={{ content: "Seleziona titoli in stato WARNING" }}
                                                        status={CreditDebitStatus.WARNING}
                                                        afterFetch={handleMassiveSelection} />
          </Space>
        </Col>
      </Row>
      <StatementTitlesTableProvider>
        <StatementTitlesTable
          tableProps={showConfirmedTitles ? { dataSource: selectedTitles,
            pagination: {
              total: selectedTitles.length,
              showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} titoli`,
              current: selectedTitlesPage,
              onChange: (page, pageSize) => {
                setSelectedTitlesPage(page);
              }
            }
          } : {}}
          defaultFilters={{ confirmed: "false", matched: true, credit_debit_status: "OK,WARNING,KO" }}
          showReconciliationColumns={true}
          rowsSelectable={true}
          selectedRows={selectedTitles.map(statement => statement.uuid)}
          onSelectedRowsChange={(keys, records) => {
            const filteredTitles = selectedTitles.filter(title => keys.includes(title.uuid));
            const allTitles = mergeTitles(filteredTitles, records.filter(Boolean));
            setSelectedTitles(allTitles);
          }} />
      </StatementTitlesTableProvider>
    </Modal>
    <Button type={"primary"} onClick={() => {
      setOpen(true);
    }} icon={<PlusOutlined />}>Crea rimessa</Button>
  </>;
};